import type {
  AgentsApi,
  DatabaseApi,
  DbPage,
  DbTableInfo,
  AgentJob,
  AgentSchedule,
  AnalyticsApi,
  HermesAccessError,
  HermesErrorReason,
  HttpApi,
  HttpError,
  HttpErrorKind,
  KeysApi,
  GraphicsApi as SdkGraphicsApi,
  ModuleContext,
  ModuleRecord,
  ModuleVersionRecord,
  PackageSource,
  PackageUpdate,
  ReviewDoc,
  WidgetApi,
  SettingSpec,
  WallpaperSettingsApi,
} from "@arclog/module-sdk";
import { ARCLOG_OS_VERSION } from "@arclog/module-sdk";
import type { OsServerEvent } from "@arclog/hermes-protocol";
import { osStore } from "./store";
import { resolveSettings } from "./settings.mjs";
import { appStateNamespace, migrateLegacyAppStateKeys } from "./user-scope.mjs";
import { getWallpaperPointer } from "./wallpaper-pointer";
import { httpErrorKindFor, buildNetGetBody } from "./net-http.mjs";
import { createGraphicsApi } from "./renderer/compositor";
import { createAgentConverse } from "./agent-converse";
import { createIconsApi } from "./icons-host";
import { dialogStore } from "./dialog";
import { appMenuStore } from "./app-menu";
import { createOsUiApi } from "./ui-host";
import { createWorkersApi } from "./workers";
import { createDeviceApi, createLayoutApi } from "./device-host";
import { createTimeApi } from "./time-host";
import { createWeatherApi } from "./weather-host";
import { createChartsApi } from "./charts";
import {
  activateTheme,
  getActiveThemeVars,
  paintCachedTheme,
  setThemeAppliedHandler,
} from "./theme-host";
import { fileDialogStore } from "./file-dialog";
import { contextMenuStore } from "./context-menu";
import { analyticsSessionId, trackEvent } from "./analytics";
import { importModuleEsm } from "./load-module";

/**
 * The compositor's GraphicsApi and the SDK's are deliberately NOT the same
 * type, and this cast is where that is admitted.
 *
 * A texture is `THREE.Texture` inside the compositor but an opaque
 * `GraphicsTexture` in the SDK — modules must never import three, so the
 * public type cannot name it. The two are structurally incompatible on
 * purpose; the cast asserts, once and in one place, that the compositor
 * satisfies the narrower public view.
 *
 * Do not "fix" this by widening GraphicsTexture to THREE.Texture. That would
 * put three in every module's type graph, which is the thing the port exists
 * to prevent.
 */
const graphicsApi = createGraphicsApi() as unknown as SdkGraphicsApi;
const iconsApi = createIconsApi();
/** Exported so host-drawn chrome (e.g. the widget settings dialog) renders with
 *  the SAME control instances modules get, not a second parallel UI kit. */
export const uiApi = createOsUiApi(iconsApi);
const deviceApi = createDeviceApi(() => osStore.uiScale);
const layoutApi = createLayoutApi(deviceApi);
const timeApi = createTimeApi();
/**
 * One instance for the whole shell, so every weather-aware module shares one
 * location, one fetch and one cache. It reaches the network through the same
 * proxy modules use — under the OS's own id, since it is the host asking, not
 * an app: nothing here uses a credential, so the id only ever appears in the
 * server's denial logs.
 */
const weatherApi = createWeatherApi((url, opts) => createHttpApi("os.weather").get(url, opts));
/** One instance: charts subscribe to theme changes through it, so the shell
 *  keeps a single registry of live charts to repaint. */
const chartsApi = createChartsApi();

setThemeAppliedHandler(() => {
  osStore.themeId = localStorage.getItem("arclog.theme") || "theme.macos-glass";
  osStore.notifyListeners();
});
try {
  paintCachedTheme();
} catch {
  /* */
}

function authHeaders(): HeadersInit {
  return osStore.token
    ? { Authorization: "Bearer " + osStore.token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

/**
 * Session self-heal. JWTs expire after 24h; when one dies mid-session every
 * authenticated call fails with 401 "Unauthorized" — including the net proxy,
 * which surfaces to apps as three simultaneous provider failures (the classic
 * "all providers failed" that is really one dead session).
 *
 * `POST /api/auth/refresh` accepts the expired-but-signed token and mints a
 * fresh 24h one (7-day grace window, claims re-derived server-side). This
 * helper runs at most ONE refresh per 30s window and coalesces concurrent
 * callers onto the same promise, so a burst of 401s costs exactly one
 * refresh round trip.
 */
let refreshing: Promise<boolean> | null = null;
let lastRefreshAt = 0;

async function refreshSessionOnce(): Promise<boolean> {
  const now = Date.now();
  if (refreshing) return refreshing;
  if (now - lastRefreshAt < 30_000) return Promise.resolve(false);
  refreshing = (async () => {
    lastRefreshAt = now;
    try {
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { Authorization: "Bearer " + (osStore.token || ""), "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.token) {
        osStore.setToken(data.token);
        if (Array.isArray(data.permissions)) osStore.setPermissions(data.permissions);
        if (data.user) localStorage.setItem("arclog.user", JSON.stringify(data.user));
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      // Release the coalescing latch shortly after settling so a LATER,
      // genuine expiry can refresh again (the 30s window guards bursts).
      setTimeout(() => {
        refreshing = null;
      }, 5_000);
    }
  })();
  return refreshing;
}

export async function login(usernameOrKey: string, password?: string): Promise<void> {
  const body =
    password != null && password !== ""
      ? { username: usernameOrKey, password }
      : usernameOrKey.toLowerCase() === "guest"
        ? { username: "guest" }
        : { key: usernameOrKey };
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Invalid credentials");
  const data = await res.json();
  osStore.setToken(data.token);
  if (Array.isArray(data.permissions)) {
    osStore.setPermissions(data.permissions);
  }
  if (data.user) {
    localStorage.setItem("arclog.user", JSON.stringify(data.user));
  }
}

export async function fetchInstallStatus(): Promise<{
  needsInstall: boolean;
  userCount: number;
  guestEnabled: boolean;
  version: string;
}> {
  const res = await fetch("/api/install/status");
  return res.json();
}

export async function completeInstall(input: {
  username: string;
  password: string;
  displayName?: string;
  enableGuest?: boolean;
  openRouterKey?: string;
}): Promise<void> {
  const res = await fetch("/api/install/complete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "install failed");
  if (data.token) {
    osStore.setToken(data.token);
    if (Array.isArray(data.permissions)) osStore.setPermissions(data.permissions);
    if (data.user) localStorage.setItem("arclog.user", JSON.stringify(data.user));
  }
}

export async function fetchPackages(): Promise<void> {
  const res = await fetch("/api/packages", { headers: authHeaders() });
  if (!res.ok) throw new Error("packages failed");
  const data = await res.json();
  osStore.setPackages(data.packages as ModuleRecord[]);
}

export async function pkgInstall(
  source: PackageSource & { version?: string; channel?: string; replace?: boolean }
): Promise<ModuleRecord> {
  const res = await fetch("/api/packages/install", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(source),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "install failed");
  await fetchPackages();
  return data;
}

export async function pkgInstallIteration(
  name: string,
  version: string,
  channel = "beta"
): Promise<ModuleRecord> {
  const res = await fetch("/api/packages/install-iteration", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ name, version, channel }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "install-iteration failed");
  await fetchPackages();
  return data;
}

export async function pkgUninstall(id: string, version?: string): Promise<void> {
  const res = await fetch(`/api/packages/${encodeURIComponent(id)}/uninstall`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(version ? { version } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "uninstall failed");
  osStore.closeAppWindows(id);
  await fetchPackages();
}

/**
 * Does this account hold `p`? The one implementation, shared by `ctx.permissions.can`
 * (what modules see) and shell chrome that gates its own buttons. Two copies of
 * "…or os.admin" would eventually disagree about who may update an app, and the
 * half that got it wrong would show a button leading to a 403.
 */
export function osCan(p: string): boolean {
  return osStore.permissions.includes(p) || osStore.permissions.includes("os.admin");
}

export async function pkgSetDefault(id: string, version: string): Promise<ModuleRecord> {
  const res = await fetch(`/api/packages/${encodeURIComponent(id)}/default`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ version }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "set-default failed");
  await fetchPackages();
  return data;
}

/**
 * Installed apps with a newer version on the shelf.
 *
 * Swallows everything, by contract: the SDK documents `ctx.packages.updates()`
 * as never rejecting, because its whole job is to decorate a UI with a badge.
 * An App Store that fails to LOAD because the update check 500'd is strictly
 * worse than one that shows no badge, and an app polling this on an interval
 * must not need a try/catch around each tick.
 */
export async function pkgUpdates(): Promise<PackageUpdate[]> {
  try {
    const res = await fetch("/api/packages/updates", { headers: authHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.updates) ? (data.updates as PackageUpdate[]) : [];
  } catch {
    return [];
  }
}

/**
 * Apply an update: install → migrate → advance the default. THE ORDER IS THE
 * FEATURE, and this function is where it is enforced, because the middle step
 * only exists here.
 *
 * `migrate(from, to, ctx)` cannot run on the server. `ModuleContext` is a
 * browser object — `ctx.appState` is localStorage, namespaced per (app,
 * account) by user-scope.mjs — so a server-side migration would be handed no
 * ctx and would find no data. The server therefore stages (installs the new
 * version beside the old, default untouched) and commits (advances the
 * default) as two calls, and the hook runs between them, here.
 *
 * The consequence worth stating plainly: if `migrate` throws, this function
 * rejects WITHOUT reaching the commit. `defaultVersion` never moved, the old
 * bundle is still what loads, and the app keeps working. Retrying is safe —
 * the server tells us whether this account already ran this exact from→to
 * hook, so a failure at the commit step does not re-run a migration that
 * already succeeded.
 */
export async function pkgUpdate(id: string): Promise<ModuleRecord> {
  const ref = encodeURIComponent(id);

  // 1 + 2. Resolve the shelf version, refuse anything not strictly newer, and
  //        install it side by side. Never a delete: the old version staying on
  //        disk is what keeps rollback a single setDefaultVersion.
  const stageRes = await fetch(`/api/packages/${ref}/update`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({}),
  });
  const staged = await stageRes.json();
  if (!stageRes.ok) throw new Error(staged.error || "update failed");
  const from = String(staged.from || "");
  const to = String(staged.to || "");

  // 3. The migration, against the NEW bundle — it is the version that knows
  //    what shape it wants. Loaded by explicit id@version rather than the bare
  //    id, which would resolve to the default and hand us the OLD code.
  if (!staged.migrated) {
    const mod = (await importModuleEsm(`/modules/${id}@${to}/index.js`)) as {
      migrate?: unknown;
      default?: { migrate?: unknown };
    };
    // Both shapes, because a module may hang the hook off its default export
    // or export it by name, and AppHost already tolerates either.
    const hook = typeof mod.migrate === "function" ? mod.migrate : mod.default?.migrate;
    if (typeof hook === "function") {
      // A normal ctx for the app being migrated — its OWN appState namespace,
      // not the caller's. `ctx.packages.update()` is usually called by the App
      // Store, and running Trading's migration against the App Store's storage
      // would migrate nothing and corrupt the wrong thing.
      const ctx = createModuleContext({ appId: id, moduleVersion: to });
      await (hook as (f: string, t: string, c: ModuleContext) => unknown)(from, to, ctx);
    }
  }

  // 4 + 5. Only now — and only because the migration RETURNED. The server
  //        records the from→to as run for this account on the same call.
  const commitRes = await fetch(`/api/packages/${ref}/update/commit`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ from, to }),
  });
  const record = await commitRes.json();
  if (!commitRes.ok) throw new Error(record.error || "update commit failed");
  await fetchPackages();

  // Open windows are still running the OLD bundle. Tell them; do not swap it.
  notifyWindowsOfUpdate(id, from, to);
  return record as ModuleRecord;
}

/**
 * Tell every open window of `appId` that a newer build is now the default, so
 * the app can OFFER a reload. Deliberately not a reload: swapping a running app
 * underneath someone mid-task is the one thing an update must never do.
 *
 * Same idiom as `arclog:app-activate` (os/store.ts) — a CustomEvent on `window`
 * carrying the window id, since that is what an app needs to know whether the
 * event is about it. `detail.reload` is the host-owned way to take the offer.
 */
function notifyWindowsOfUpdate(appId: string, from: string, to: string) {
  for (const w of osStore.windows.filter((x) => x.appId === appId)) {
    window.dispatchEvent(
      new CustomEvent("arclog:app-updated", {
        detail: {
          appId,
          windowId: w.id,
          from,
          to,
          reload: () => reloadWindowAfterUpdate(w.id, appId),
        },
      })
    );
  }
}

/**
 * Reload one window onto the new default build, ASKING FIRST.
 *
 * `requestCloseWindow` rather than `closeWindow` is the whole point: it runs
 * the window's close guard, and `ctx.windows.setDirty(true)` installs exactly
 * such a guard. So an app holding unsaved work can refuse, and the refusal
 * costs nothing — it keeps running the old bundle, which is still installed and
 * still works. Returns false so a caller can leave its "update ready" affordance
 * on screen instead of assuming the reload happened.
 */
async function reloadWindowAfterUpdate(windowId: string, appId: string): Promise<boolean> {
  const win = osStore.windows.find((w) => w.id === windowId);
  if (!win) return false;
  const { title, params } = win;
  if (!(await osStore.requestCloseWindow(windowId))) return false;
  // Reopened with NO version: a window that pinned the build it happened to be
  // running would never see this update, or the next one.
  osStore.openApp(appId, title, undefined, params);
  return true;
}

export async function pkgVersions(id: string) {
  const res = await fetch(`/api/packages/${encodeURIComponent(id)}/versions`, {
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "versions failed");
  // The route hands back manager.listVersions(id), which is typed
  // ModuleVersionRecord[] — full records, manifest and install paths included.
  // This used to assert a three-field subset, which quietly made ctx.packages
  // narrower than the SDK promised.
  return data.versions as ModuleVersionRecord[];
}

type StreamHandler = (ev: {
  type: "stream" | "message" | "error";
  content?: string;
  delta?: string;
  role?: string;
  id?: string;
  error?: string;
  /** Typed refusal, forwarded verbatim from `hermes.error`. */
  reason?: HermesErrorReason;
}) => void;

class OsSocket {
  private ws: WebSocket | null = null;
  private hermesHandlers = new Set<StreamHandler>();
  private agentHandlers = new Set<(ev: { type: string; [k: string]: unknown }) => void>();
  private reconnectTimer: number | null = null;

  connect() {
    if (!osStore.token) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws.onopen = () => {
      this.ws?.send(JSON.stringify({ type: "session.hello", token: osStore.token }));
    };
    this.ws.onmessage = (ev) => {
      let msg: OsServerEvent;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (msg.type === "packages.changed") {
        osStore.setPackages(msg.packages);
      } else if (msg.type === "agent.job") {
        for (const h of this.agentHandlers) h({ type: "agent.job", job: msg.job });
      } else if (msg.type === "agent.message") {
        for (const h of this.agentHandlers) h({ type: "agent.message", message: msg.message });
      } else if (msg.type === "agent.progress") {
        for (const h of this.agentHandlers)
          h({ type: "agent.progress", jobId: msg.jobId, agentId: msg.agentId, text: msg.text });
      } else if (msg.type === "agent.delta") {
        for (const h of this.agentHandlers)
          h({ type: "agent.delta", jobId: msg.jobId, agentId: msg.agentId, seq: msg.seq, text: msg.text, done: msg.done });
      } else if (msg.type === "hermes.stream") {
        for (const h of this.hermesHandlers) h({ type: "stream", delta: msg.delta, id: msg.id });
      } else if (msg.type === "hermes.message") {
        for (const h of this.hermesHandlers)
          h({ type: "message", content: msg.content, role: msg.role, id: msg.id });
      } else if (msg.type === "hermes.error") {
        // Forward `reason`, don't drop it: it is the difference between an app
        // saying "add your OpenRouter key" and an app saying nothing useful.
        for (const h of this.hermesHandlers)
          h({ type: "error", error: msg.error, reason: msg.reason });
      }
    };
    this.ws.onclose = () => {
      if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = window.setTimeout(() => this.connect(), 1500);
    };
  }

  /**
   * Re-authenticate the socket with the (possibly refreshed) session token.
   * Called after a successful session refresh: the live WS authenticated
   * with the old token, and the server validates on `session.hello`, so we
   * drop it and reconnect — the next `onopen` sends the fresh token.
   */
  reconnectSoon(): void {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    try {
      this.ws?.close();
    } catch {
      /* */
    }
    this.reconnectTimer = window.setTimeout(() => this.connect(), 250);
  }

  sendHermes(sessionId: string, content: string) {
    this.ws?.send(JSON.stringify({ type: "hermes.send", sessionId, content }));
  }

  onHermes(handler: StreamHandler) {
    this.hermesHandlers.add(handler);
    return () => this.hermesHandlers.delete(handler);
  }

  /** Agent activity (job state changes, messages). Returns unsubscribe. */
  onAgentEvent(handler: (ev: { type: string; [k: string]: unknown }) => void) {
    this.agentHandlers.add(handler);
    return () => this.agentHandlers.delete(handler);
  }
}

export const osSocket = new OsSocket();

/* ── ctx.http — outbound GET via the OS proxy ──────────────────────────────
 * Everything a module sends outward goes through POST /api/net/get. The shell
 * adds no cleverness here on purpose: no retries, no caching (the server caches
 * per host TTL), no header pass-through. All the policy lives on the server,
 * where it is enforceable.
 * ------------------------------------------------------------------------- */

/** Thrown by ctx.http.get. `kind` is what a widget should branch on. */
class HttpRequestError extends Error implements HttpError {
  kind: HttpErrorKind;
  status: number;
  reason?: string;

  constructor(kind: HttpErrorKind, status: number, reason?: string, message?: string) {
    super(message || `http ${kind}${reason ? `: ${reason}` : ""}`);
    this.name = "HttpRequestError";
    this.kind = kind;
    this.status = status;
    this.reason = reason;
  }
}

function createHttpApi(appId: string): HttpApi {
  return {
    get: async (url: string, opts?: { timeoutMs?: number; useKey?: string }) => {
      const doFetch = (): Promise<Response> =>
        fetch("/api/net/get", {
          method: "POST",
          headers: authHeaders(),
          // appId comes from the host's own module record, NEVER from the
          // caller: an app that could name another app's id would borrow its
          // credentials. See createModuleContext, which closes over this same
          // value for ctx.analytics/ctx.appState/ctx.logs — and net-http.mjs's
          // own comment on buildNetGetBody for why `opts` can't smuggle one in.
          body: JSON.stringify(buildNetGetBody(appId, url, opts)),
        });

      let res: Response;
      try {
        res = await doFetch();
        // SESSION SELF-HEAL: a 401 here almost always means the session JWT
        // expired mid-session (24h TTL). Refresh once and retry the SAME
        // request transparently — without this, every app's data fetches die
        // together ("all providers failed … Unauthorized") until the user
        // logs in again. If the refresh itself fails, the original 401
        // response flows on to the error path below unchanged.
        if (res.status === 401 && osStore.token) {
          const ok = await refreshSessionOnce();
          if (ok) {
            osSocket.reconnectSoon();
            res = await doFetch();
          }
        }
      } catch (e) {
        // The OS itself was unreachable — never confuse this with a denial.
        throw new HttpRequestError("network", 0, "OS_UNREACHABLE", (e as Error)?.message);
      }

      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        status?: number;
        url?: string;
        body?: string;
        error?: string;
        reason?: string;
      } | null;

      if (!res.ok) {
        throw new HttpRequestError(
          httpErrorKindFor(res.status, data?.reason),
          res.status,
          data?.reason || data?.error,
        );
      }

      const body = typeof data?.body === "string" ? data.body : "";
      return {
        ok: data?.ok === true,
        status: Number(data?.status) || 0,
        // The request URL as the server saw it — never an upstream redirect.
        url: typeof data?.url === "string" ? data.url : url,
        json: async <T = unknown,>() => JSON.parse(body) as T,
        text: async () => body,
      };
    },
  };
}

/**
 * `ctx.keys` — whether a credential is set, and a way to send the user to go
 * set one. There is no `get`: the value never reaches the browser at all, so
 * there is nothing here for app code to read, log or forward.
 *
 * Unlike `ctx.http`, this needs no `appId`: `GET /api/keys` answers for the
 * signed-in *user* (which credentials they could set, and whether they have),
 * not for a specific app's grant — the app-scoping happens later, in
 * `resolveNetCredential`, when a credential is actually spent via `useKey`.
 */
/**
 * POST a credential value (or username/password pair) to the KeyStore.
 * `suffix` = "" (personal) or "/system" (admin); only the id segment is
 * encoded, the suffix is a fixed literal. Sent straight to the server; never
 * persisted client-side. Throws with the server's own message on rejection.
 */
async function postKey(
  id: string,
  suffix: string,
  body: { value?: string; username?: string; password?: string },
): Promise<void> {
  const res = await fetch(`/api/keys/${encodeURIComponent(id)}${suffix}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(err?.error || `HTTP ${res.status}`);
  }
}

/** DELETE one KeyStore record. `suffix` = "" (personal) or "/system" (admin). */
async function deleteKey(id: string, suffix: string): Promise<void> {
  const res = await fetch(`/api/keys/${encodeURIComponent(id)}${suffix}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(err?.error || `HTTP ${res.status}`);
  }
}

function createKeysApi(): KeysApi {
  return {
    async has(id: string): Promise<boolean> {
      const res = await fetch("/api/keys", { headers: authHeaders() });
      if (!res.ok) return false;
      const body = (await res.json().catch(() => null)) as {
        credentials?: Array<{ id: string; set: boolean }>;
      } | null;
      return Boolean(body?.credentials?.find((c) => c.id === id)?.set);
    },
    prompt(id: string): void {
      // The KeyChain app is the single home for credentials. It receives the
      // credential id as a launch param and focuses (or opens) that row.
      osStore.openApp("app.keychain", "KeyChain", undefined, { credential: id });
    },
    async list(): Promise<
      Array<{
        id: string;
        label: string;
        help: string;
        scope: string;
        kind: string;
        set: boolean;
        systemSet?: boolean;
        appId?: string;
        appName?: string;
      }>
    > {
      try {
        const res = await fetch("/api/keys", { headers: authHeaders() });
        if (!res.ok) return [];
        const body = (await res.json().catch(() => null)) as {
          credentials?: Array<{
            id: string;
            label: string;
            help: string;
            scope: string;
            kind: string;
            set: boolean;
            systemSet?: boolean;
            appId?: string;
            appName?: string;
          }>;
        } | null;
        return Array.isArray(body?.credentials) ? body!.credentials : [];
      } catch {
        return [];
      }
    },
    async set(id: string, value: string): Promise<void> {
      await postKey(id, "", { value });
    },
    async setLogin(id: string, username: string, password: string): Promise<void> {
      await postKey(id, "", { username, password });
    },
    async clear(id: string): Promise<void> {
      await deleteKey(id, "");
    },
    async setSystem(id: string, value: string): Promise<void> {
      await postKey(id, "/system", { value });
    },
    async setSystemLogin(id: string, username: string, password: string): Promise<void> {
      await postKey(id, "/system", { username, password });
    },
    async clearSystem(id: string): Promise<void> {
      await deleteKey(id, "/system");
    },
  };
}

/* ── ctx.agents — shared per-account AI agents ─────────────────────────────
 * REST for command/query, the OS socket for live events. `appId` is stamped
 * by the host (the module record this context was created for), never taken
 * from the caller — same trust rule as appState namespacing.
 *
 * subscribe() polls; the socket also pushes job changes, so subscribers get
 * near-instant updates when the window is focused and a poll-backstop when it
 * is not. The returned unsubscribe stops BOTH.
 */
async function agentsFetch<T>(path: string, init?: RequestInit): Promise<T | null> {
  const res = await fetch(`/api/agents${path}`, {
    ...init,
    headers: { ...(init?.headers || {}), ...authHeaders() },
  });
  if (!res.ok) return null;
  return (await res.json().catch(() => null)) as T | null;
}

/* ── ctx.db — the app's own relational database ──────────────────────────
 * Scoped to `app.<thisApp>`: the host stamps the name from the module record
 * this context was created for (same trust rule as appState namespacing),
 * never from caller input. The server creates the database lazily on first
 * write; reads before that return empty results rather than errors.
 */
async function dbFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/db${path}`, {
    ...init,
    headers: { ...(init?.headers || {}), ...authHeaders(), "Content-Type": "application/json" },
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(typeof body.error === "string" ? body.error : `db ${res.status}`);
  }
  return body as T;
}

function createDbApi(appId: string): DatabaseApi {
  const name = appId && appId !== "app.unknown" ? appId : "app.unknown";
  const enc = encodeURIComponent;

  // Empty result for "the database does not exist yet" — first read is never
  // an error; the store materializes on first create/insert.
  const emptyPage = (): DbPage => ({ columns: [], rows: [], total: 0, limit: 0, offset: 0 });

  async function with404AsEmpty<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      if (e instanceof Error && /database not found/i.test(e.message)) return fallback;
      throw e;
    }
  }

  return {
    name,
    tables: () =>
      with404AsEmpty(
        async () => (await dbFetch<{ tables: DbTableInfo[] }>(`/${enc(name)}/tables`)).tables,
        [] as DbTableInfo[]
      ),
    createTable: (table, columns) =>
      dbFetch(`/${enc(name)}/tables`, { method: "POST", body: JSON.stringify({ table, columns }) }).then(() => {}),
    dropTable: (table) => dbFetch(`/${enc(name)}/tables/${enc(table)}`, { method: "DELETE" }).then(() => {}),
    addColumn: (table, column) =>
      dbFetch(`/${enc(name)}/tables/${enc(table)}/columns`, { method: "POST", body: JSON.stringify(column) }).then(
        () => {}
      ),
    query: (table, q) =>
      with404AsEmpty(
        () => dbFetch<DbPage>(`/${enc(name)}/tables/${enc(table)}/query`, { method: "POST", body: JSON.stringify(q || {}) }),
        emptyPage()
      ),
    insert: (table, rows) =>
      dbFetch<{ inserted: number }>(`/${enc(name)}/tables/${enc(table)}/rows`, {
        method: "POST",
        body: JSON.stringify({ rows: Array.isArray(rows) ? rows : [rows] }),
      }).then((r) => r.inserted),
    update: (table, patch, where) =>
      dbFetch<{ updated: number }>(`/${enc(name)}/tables/${enc(table)}/rows`, {
        method: "PATCH",
        body: JSON.stringify({ patch, where }),
      }).then((r) => r.updated),
    delete: (table, where) =>
      dbFetch<{ deleted: number }>(`/${enc(name)}/tables/${enc(table)}/rows`, {
        method: "DELETE",
        body: JSON.stringify({ where }),
      }).then((r) => r.deleted),
    count: (table, where) =>
      dbFetch<{ total: number }>(`/${enc(name)}/tables/${enc(table)}/query`, {
        method: "POST",
        body: JSON.stringify({ where: where || {}, limit: 1 }),
      }).then((r) => r.total),
    sql: (sql, params) =>
      dbFetch<DbPage>(`/${enc(name)}/sql`, { method: "POST", body: JSON.stringify({ sql, params: params || [] }) }),
  };
}

function createAgentsApi(
  appId: string,
  getCtx?: () => ModuleContext | null
): AgentsApi {
  const hostAppId = appId && appId !== "app.unknown" ? appId : "app.unknown";
  const POLL_MS = 2500;

  return {
    async list() {
      const body = await agentsFetch<{ agents: Awaited<ReturnType<AgentsApi["list"]>> }>("/");
      return body?.agents || [];
    },
    async run(req) {
      const body = await agentsFetch<{ jobId: string; status: AgentJob["status"] }>("/run", {
        method: "POST",
        body: JSON.stringify({ ...req, appId: hostAppId }),
      });
      if (!body) throw new Error("agents.run failed (missing permission or server error)");
      return body;
    },
    async status(jobId) {
      const body = await agentsFetch<{ job: AgentJob }>(`/jobs/${encodeURIComponent(jobId)}`);
      return body?.job ?? null;
    },
    async result(jobId) {
      return this.status(jobId);
    },
    async cancel(jobId) {
      const body = await agentsFetch<{ job: AgentJob }>(`/jobs/${encodeURIComponent(jobId)}/cancel`, {
        method: "POST",
      });
      return Boolean(body?.job);
    },
    subscribe(jobId, cb) {
      let stopped = false;
      let timer: number | null = null;
      let lastStatus = "";
      const deliver = (job: AgentJob | null) => {
        if (!job || stopped) return;
        if (job.status !== lastStatus || job.status === "running") {
          lastStatus = job.status;
          cb(job);
        }
        if (job.status === "succeeded" || job.status === "failed" || job.status === "cancelled") {
          stop();
        }
      };
      const poll = async () => {
        if (stopped) return;
        deliver(await this.status(jobId));
        if (!stopped) timer = window.setTimeout(poll, POLL_MS);
      };
      const offSocket = osSocket.onAgentEvent((ev) => {
        if (ev.type !== "agent.job") return;
        const job = ev.job as AgentJob;
        if (job?.id === jobId) deliver(job);
      });
      const stop = () => {
        stopped = true;
        if (timer) window.clearTimeout(timer);
        offSocket();
      };
      void poll();
      return stop;
    },
    async jobs(agentId, limit) {
      const q = new URLSearchParams();
      if (agentId) q.set("agent", agentId);
      if (limit) q.set("limit", String(limit));
      const qs = q.toString();
      const body = await agentsFetch<{ jobs: AgentJob[] }>(`/jobs${qs ? `?${qs}` : ""}`);
      return body?.jobs || [];
    },
    async message(agentId, text, opts) {
      const body = await agentsFetch<{ ok: boolean; jobId?: string | null }>("/message", {
        method: "POST",
        // appId tells the server which app's tools + mirrored state the
        // reply job should run with — Trading messages get Trading tools.
        // stream defaults ON; opts.stream === false opts out of deltas.
        body: JSON.stringify({ to: agentId, text, appId: hostAppId, stream: opts?.stream !== false }),
      });
      // Hand back the reply job id so the app can subscribe and refresh
      // when the agent finishes (e.g. after designing a strategy).
      return body?.jobId ?? null;
    },
    async messages(agentId, since) {
      const q = new URLSearchParams();
      if (agentId) q.set("with", agentId);
      if (since) q.set("since", String(since));
      const qs = q.toString();
      const body = await agentsFetch<{ messages: Awaited<ReturnType<AgentsApi["messages"]>> }>(
        `/messages${qs ? `?${qs}` : ""}`
      );
      return body?.messages || [];
    },
    subscribeMessages(cb) {
      return osSocket.onAgentEvent((ev) => {
        if (ev.type === "agent.message") {
          cb(ev.message as Parameters<typeof cb>[0]);
        }
      });
    },
    subscribeProgress(cb, jobId) {
      return osSocket.onAgentEvent((ev) => {
        if (ev.type !== "agent.progress") return;
        const p = ev as unknown as { jobId: string; agentId: string; text: string };
        if (jobId && p.jobId !== jobId) return;
        cb(p);
      });
    },
    subscribeDelta(cb, jobId) {
      return osSocket.onAgentEvent((ev) => {
        if (ev.type !== "agent.delta") return;
        const d = ev as unknown as { jobId: string; agentId: string; seq: number; text: string; done: boolean };
        if (jobId && d.jobId !== jobId) return;
        cb(d);
      });
    },
    converse(opts) {
      const ctx = getCtx?.();
      if (!ctx) throw new Error("ctx.agents.converse: module context not ready yet");
      return createAgentConverse(ctx, opts || {});
    },
    memory: {
      async get(agentId) {
        const body = await agentsFetch<{ memory: Awaited<ReturnType<AgentsApi["memory"]["get"]>> }>(
          `/memory/${encodeURIComponent(agentId)}`
        );
        return body?.memory ?? null;
      },
      async setFact(agentId, key, value) {
        await agentsFetch(`/memory/${encodeURIComponent(agentId)}/fact`, {
          method: "POST",
          body: JSON.stringify({ key, value }),
        });
      },
      async append(agentId, entry) {
        await agentsFetch(`/memory/${encodeURIComponent(agentId)}/journal`, {
          method: "POST",
          body: JSON.stringify(entry),
        });
      },
    },
    skills: {
      async list() {
        const body = await agentsFetch<{ skills: Awaited<ReturnType<AgentsApi["skills"]["list"]>> }>("/skills");
        return body?.skills || [];
      },
      async get(id) {
        const body = await agentsFetch<{ body: string }>(`/skills/${id}`);
        return body?.body ?? null;
      },
      async write(id, body) {
        const res = await agentsFetch<{ ok: boolean }>(`/skills/${id}`, {
          method: "PUT",
          body: JSON.stringify({ body }),
        });
        return Boolean(res?.ok);
      },
      async remove(id) {
        const res = await agentsFetch<{ ok: boolean }>(`/skills/${id}`, { method: "DELETE" });
        return Boolean(res?.ok);
      },
    },
    orchestrator: {
      async info() {
        const body = await agentsFetch<{ orchestrator: Awaited<ReturnType<AgentsApi["orchestrator"]["info"]>> }>(
          "/orchestrator"
        );
        return (
          body?.orchestrator || { kind: "none", id: "", mode: "rules" }
        );
      },
    },
    reviews: {
      async list(opts) {
        const q = new URLSearchParams();
        if (opts?.projectId) q.set("project", opts.projectId);
        if (opts?.kind) q.set("kind", opts.kind);
        const qs = q.toString();
        const body = await agentsFetch<{ docs: Awaited<ReturnType<AgentsApi["reviews"]["list"]>> }>(
          `/reviews${qs ? `?${qs}` : ""}`
        );
        return body?.docs || [];
      },
      async get(id) {
        const body = await agentsFetch<{ doc: Awaited<ReturnType<AgentsApi["reviews"]["get"]>> }>(
          `/reviews/${encodeURIComponent(id)}`
        );
        return body?.doc ?? null;
      },
      async create(doc) {
        const body = await agentsFetch<{ doc: ReviewDoc }>("/reviews", {
          method: "POST",
          body: JSON.stringify(doc),
        });
        if (!body?.doc) throw new Error("review doc create failed");
        return body.doc;
      },
      async update(id, patch) {
        const body = await agentsFetch<{ doc: ReviewDoc }>(`/reviews/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        });
        return body?.doc ?? null;
      },
      async remove(id) {
        const body = await agentsFetch<{ ok: boolean }>(`/reviews/${encodeURIComponent(id)}`, {
          method: "DELETE",
        });
        return Boolean(body?.ok);
      },
      async projects() {
        const body = await agentsFetch<{ projects: Awaited<ReturnType<AgentsApi["reviews"]["projects"]>> }>(
          "/reviews/projects"
        );
        return body?.projects || [];
      },
    },
    schedules: {
      async list() {
        const body = await agentsFetch<{ schedules: Awaited<ReturnType<AgentsApi["schedules"]["list"]>> }>("/schedules");
        return body?.schedules || [];
      },
      async create(s) {
        const body = await agentsFetch<{ schedule: AgentSchedule }>("/schedules", {
          method: "POST",
          body: JSON.stringify({ ...s, appId: s.appId || hostAppId }),
        });
        if (!body?.schedule) throw new Error("schedule create failed");
        return body.schedule;
      },
      async setEnabled(id, enabled) {
        const body = await agentsFetch<{ schedule: AgentSchedule }>(
          `/schedules/${encodeURIComponent(id)}/enable`,
          { method: "POST", body: JSON.stringify({ enabled }) }
        );
        return body?.schedule ?? null;
      },
      async remove(id) {
        const body = await agentsFetch<{ ok: boolean }>(`/schedules/${encodeURIComponent(id)}`, {
          method: "DELETE",
        });
        return Boolean(body?.ok);
      },
    },
    source: {
      async list(moduleId) {
        const body = await agentsFetch<{ files: Awaited<ReturnType<AgentsApi["source"]["list"]>> }>(
          `/source/${encodeURIComponent(moduleId)}`
        );
        return body?.files || [];
      },
      async read(moduleId, path) {
        const body = await agentsFetch<{ content: string; truncated: boolean }>(
          `/source/${encodeURIComponent(moduleId)}/${path}`
        );
        return body ?? null;
      },
    },
    persona: {
      async read(agentId) {
        const body = await agentsFetch<Awaited<ReturnType<AgentsApi["persona"]["read"]>>>(
          `/persona/${encodeURIComponent(agentId)}`
        );
        return body ?? null;
      },
      async writePersona(agentId, file, content) {
        const body = await agentsFetch<{ ok: boolean }>(
          `/persona/${encodeURIComponent(agentId)}/${file}`,
          { method: "PUT", body: JSON.stringify({ content }) }
        );
        return Boolean(body?.ok);
      },
      async readNote(agentId, name) {
        const body = await agentsFetch<{ content: string | null }>(
          `/persona/${encodeURIComponent(agentId)}/notes/${encodeURIComponent(name)}`
        );
        return body?.content ?? null;
      },
      async writeNote(agentId, name, content) {
        const body = await agentsFetch<{ ok: boolean }>(
          `/persona/${encodeURIComponent(agentId)}/notes/${encodeURIComponent(name)}`,
          { method: "PUT", body: JSON.stringify({ content }) }
        );
        return Boolean(body?.ok);
      },
      async setHeartbeat(agentId, cfg) {
        const body = await agentsFetch<{ enabled: boolean; cadence: string; prompt: string }>(
          `/persona/${encodeURIComponent(agentId)}/heartbeat`,
          { method: "POST", body: JSON.stringify(cfg || {}) }
        );
        return body ?? null;
      },
      async generate(agentId, opts) {
        const body = await agentsFetch<{
          persona: string;
          soul: string;
          source: "llm" | "rules";
          saved: boolean;
        }>(`/persona/${encodeURIComponent(agentId)}/generate`, {
          method: "POST",
          body: JSON.stringify(opts || {}),
        });
        return body ?? null;
      },
    },
    async update(agentId, patch) {
      const body = await agentsFetch<{ agent?: unknown }>(`/${encodeURIComponent(agentId)}`, {
        method: "PATCH",
        body: JSON.stringify(patch || {}),
      });
      return Boolean(body?.agent);
    },
    async tools(agentId) {
      return await agentsFetch<Awaited<ReturnType<AgentsApi["tools"]>>>(`/${encodeURIComponent(agentId)}/tools`);
    },
    async logs(agentId, limit) {
      return await agentsFetch<Awaited<ReturnType<AgentsApi["logs"]>>>(
        `/${encodeURIComponent(agentId)}/logs${limit ? `?limit=${limit}` : ""}`
      );
    },
    async insight(agentId, limit) {
      return await agentsFetch<Awaited<ReturnType<AgentsApi["insight"]>>>(
        `/${encodeURIComponent(agentId)}/insight${limit ? `?limit=${limit}` : ""}`
      );
    },
  };
}

/**
 * `ctx.analytics` — usage events, stamped with the calling app's id the same
 * way `createLogsApi` stamps `source`, so an app cannot report as another one.
 *
 * Synchronous and void: events go into the shell's batch buffer, not onto the
 * wire. An app should never await, retry or error-handle an analytics call.
 */
function createAnalyticsApi(appId: string): AnalyticsApi {
  const id = appId && appId !== "app.unknown" ? appId : "app";
  return {
    track: (event: string, props?: Record<string, unknown>) => {
      trackEvent(event, { appId: id, props });
    },
    sessionId: () => analyticsSessionId(),
  };
}

function createLogsApi(appId: string) {
  const src = () => (appId && appId !== "app.unknown" ? appId : "app");
  return {
    write: async (o: {
      message: string;
      level?: "debug" | "info" | "warn" | "error";
      source?: string;
      code?: string;
      detail?: unknown;
    }) => {
      const res = await fetch("/api/system/logs", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          message: o.message,
          level: o.level || "info",
          source: o.source || src(),
          code: o.code,
          detail: o.detail,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      return data.entry;
    },
    info: async (message: string, detail?: unknown) => {
      const res = await fetch("/api/system/logs", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ message, level: "info", source: src(), detail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      return data.entry;
    },
    warn: async (message: string, detail?: unknown) => {
      const res = await fetch("/api/system/logs", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ message, level: "warn", source: src(), detail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      return data.entry;
    },
    error: async (message: string, detail?: unknown) => {
      const res = await fetch("/api/system/logs", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ message, level: "error", source: src(), detail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      return data.entry;
    },
    query: async (opts?: {
      limit?: number;
      level?: string;
      source?: string;
      contains?: string;
      since?: string;
    }) => {
      const q = new URLSearchParams();
      if (opts?.limit) q.set("limit", String(opts.limit));
      if (opts?.level) q.set("level", opts.level);
      if (opts?.source) q.set("source", opts.source);
      if (opts?.contains) q.set("q", opts.contains);
      if (opts?.since) q.set("since", opts.since);
      const res = await fetch("/api/system/logs?" + q.toString(), {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      return { entries: data.entries || [], totalMatched: data.totalMatched || 0 };
    },
  };
}

type WidgetOpts = {
  size: { cols: number; rows: number };
  on: "desktop" | "panel";
  /** Placement id ("it_<n>") — settings are stored per placement, not per module. */
  placementId: string;
  /** manifest.widget.settings of the mounted module; resolves stored → default. */
  settings?: SettingSpec[];
  onResize(cb: (s: { cols: number; rows: number }) => void): () => void;
  /**
   * Host-owned teardown registry. `settings.onChange` subscribes to osStore,
   * whose listener set OUTLIVES the widget — a module that forgets to call the
   * returned unsubscribe would leak a callback firing on every store change
   * forever. The host hands this in and disposes everything on unmount, the
   * same defensive stance as WidgetHost's partial-mount teardown.
   */
  registerCleanup?(dispose: () => void): void;
};

/** ctx.widget for a mounted placement; undefined for the full-app surface. */
/** Schema of the wallpaper that is active RIGHT NOW — it can change under us. */
function activeWallpaperSpecs(): SettingSpec[] {
  const id = osStore.wallpaperId;
  const m = osStore.wallpaperModules().find((x) => x.id === id);
  return m?.wallpaper?.settings || [];
}

/**
 * `type:"button"` presses, keyed by wallpaper module id. A button STORES
 * NOTHING — see `SettingSpec` — so it cannot ride the path below that diffs
 * the PERSISTED bag on every `osStore` change; it never touches that bag at
 * all. This is the separate, non-persisting channel the wallpaper settings
 * panel uses instead, so the SAME `ctx.wallpaper.settings.onChange` a module
 * already listens on can still observe a press.
 */
type WallpaperActionListener = (moduleId: string, key: string) => void;
const wallpaperActionListeners = new Set<WallpaperActionListener>();

/**
 * Host-only entry point: the wallpaper settings panel calls this on a
 * `type:"button"` click. Never call this for anything that should be
 * remembered — it writes to no store and nothing reads it back later.
 */
export function fireWallpaperAction(moduleId: string, key: string): void {
  for (const l of wallpaperActionListeners) l(moduleId, key);
}

/**
 * Settings for the ACTIVE wallpaper, keyed by module id.
 *
 * Deliberately re-reads `osStore.wallpaperId` on every call rather than
 * closing over it: a ctx outlives a wallpaper switch, and a stale id would
 * write one wallpaper's values into another's bag.
 */
function buildWallpaperSettingsApi(): WallpaperSettingsApi {
  const getAll = () =>
    resolveSettings(activeWallpaperSpecs(), osStore.getWallpaperSettings(osStore.wallpaperId));
  const isReload = (k: string) =>
    activeWallpaperSpecs().some((s) => s.key === k && s.reload === true);

  return {
    get: <T = unknown,>(k: string) => getAll()[k] as T | undefined,
    getAll,
    set: (k: string, v: unknown) =>
      osStore.setWallpaperSetting(osStore.wallpaperId, k, v, isReload(k)),
    reset: () => osStore.resetWallpaperSettings(osStore.wallpaperId),
    onChange: (cb: (values: Record<string, unknown>) => void) => {
      /* Diff only the LIVE keys. A `reload` key changing arrives as a fresh
         mount, so firing onChange for it would hand the module a value it
         cannot apply in place — and it would fire in the same tick as the
         teardown. The callback still receives the FULL bag, so a module reading
         values.count gets a real number rather than a hole. */
      const liveOnly = () => {
        const all = getAll();
        const out: Record<string, unknown> = {};
        for (const s of activeWallpaperSpecs()) if (!s.reload) out[s.key] = all[s.key];
        return out;
      };
      // osStore.subscribe fires on ANY store change (window moves, toasts, …),
      // so diff before notifying or every wallpaper repaints on unrelated
      // OS activity.
      let last = JSON.stringify(liveOnly());
      const unsubStore = osStore.subscribe(() => {
        const ser = JSON.stringify(liveOnly());
        if (ser === last) return;
        last = ser;
        cb(getAll());
      });
      // A button press never touches the store, so it cannot reach the
      // listener above — hand it to the SAME cb directly instead, once, as
      // `{ ...current values, [key]: true }`. This does not update `last`:
      // the flag never really entered the bag, so the next real store change
      // must still be diffed against the bag as it stood before the press.
      const onAction: WallpaperActionListener = (moduleId, key) => {
        if (moduleId !== osStore.wallpaperId) return;
        cb({ ...getAll(), [key]: true });
      };
      wallpaperActionListeners.add(onAction);
      return () => {
        unsubStore();
        wallpaperActionListeners.delete(onAction);
      };
    },
  };
}

function buildWidgetApi(appId: string, w?: WidgetOpts): WidgetApi | undefined {
  if (!w) return undefined;
  const { placementId, settings: specs } = w;
  const getAll = () => resolveSettings(specs, osStore.getWidgetSettings(placementId));
  return {
    openAsApp: () => {
      osStore.openApp(appId);
    },
    size: w.size,
    surface: w.on,
    onResize: w.onResize,
    settings: {
      get: <T = unknown,>(k: string) => getAll()[k] as T | undefined,
      getAll,
      set: (k: string, v: unknown) => osStore.setWidgetSetting(placementId, k, v),
      reset: () => osStore.resetWidgetSettings(placementId),
      // osStore.subscribe fires on ANY store change (window moves, toasts, …), so
      // diff the resolved values and only notify on a real change — otherwise every
      // widget re-renders on unrelated OS activity.
      onChange: (cb: (values: Record<string, unknown>) => void) => {
        let last = JSON.stringify(getAll());
        const raw = osStore.subscribe(() => {
          const next = getAll();
          const ser = JSON.stringify(next);
          if (ser === last) return;
          last = ser;
          cb(next);
        });
        // Idempotent: the module may call this itself AND the host will call it
        // on unmount. Double-unsubscribing must be a no-op, not a second
        // delete against a listener set that may have been re-populated.
        let disposed = false;
        const dispose = () => {
          if (disposed) return;
          disposed = true;
          raw();
        };
        w.registerCleanup?.(dispose);
        return dispose;
      },
    },
  };
}

export function createModuleContext(opts?: {
  windowId?: string;
  moduleVersion?: string;
  appId?: string;
  surface?: "window" | "widget";
  widget?: WidgetOpts;
  /** Params this window was opened with (osStore.openApp's 4th argument). */
  launchParams?: Record<string, unknown>;
  /**
   * Host-owned teardown registry for launchParams.subscribe, mirroring
   * WidgetOpts.registerCleanup. Subscribers live in osStore, which outlives the
   * mount, so the host disposes them on unmount rather than trusting the app to.
   */
  registerCleanup?(dispose: () => void): void;
}) {
  const windowId = opts?.windowId;
  const appId = opts?.appId || "app.unknown";
  // Account-scoped namespace (user-scope.mjs): one (app, account) pair gets its
  // own key space, so on a shared browser one account's appState never reads
  // back under another account. Legacy unscoped keys are migrated into the
  // first real account to open the app after the upgrade.
  const stateNs = appStateNamespace(appId, osStore.accountScope);
  try {
    migrateLegacyAppStateKeys(localStorage, appId, osStore.accountScope);
  } catch {
    /* disabled/full storage must not stop the app mounting */
  }
  // ctx.widget exists ONLY on the widget surface — a full-app mount has no
  // placement, so there is nothing for settings to key off.
  const widgetApi = buildWidgetApi(appId, opts?.surface === "widget" ? opts?.widget : undefined);
  // Lazy self-reference: ctx.agents.converse needs the FULL context (notify,
  // agents API itself) but is built INSIDE this literal. The holder closes
  // the loop once the object exists.
  let ctxHolder: ModuleContext | null = null;
  const ctx = {
    bus: {
      on: () => () => {},
      emit: () => {},
    },
    windows: {
      open: (
        appId: string,
        o?: { title?: string; version?: string; params?: Record<string, unknown> }
      ) => osStore.openApp(appId, o?.title, o?.version, o?.params),
      close: (id: string) => void osStore.requestCloseWindow(id),
      focus: (id: string) => osStore.focus(id),
      setTitle: (title: string) => {
        if (windowId) osStore.updateWindow(windowId, { title });
      },
      /**
       * Veto closing this window. Return false to keep it open. Runs for the
       * traffic light, the window menu and ctx.windows.close.
       */
      setCloseGuard: (guard: (() => boolean | Promise<boolean>) | null) => {
        if (windowId) osStore.setCloseGuard(windowId, guard);
      },
      /**
       * One-liner for the common case: while dirty, closing asks to confirm.
       * Call setDirty(false) after saving.
       */
      setDirty: (
        dirty: boolean,
        opts?: { title?: string; message?: string; discardLabel?: string }
      ) => {
        if (!windowId) return;
        if (!dirty) {
          osStore.setCloseGuard(windowId, null);
          return;
        }
        osStore.setCloseGuard(windowId, () =>
          dialogStore.confirm({
            title: opts?.title || "Unsaved changes",
            message:
              opts?.message ||
              "This window has unsaved changes. Close it and lose them?",
            okLabel: opts?.discardLabel || "Discard changes",
            cancelLabel: "Keep editing",
            danger: true,
          })
        );
      },
      currentId: windowId,
    },
    /**
     * Why the window was opened — `{ path }` when the OS routed a file here via
     * ctx.shell.openPath, whatever the caller passed otherwise.
     *
     * `subscribe` is the half that matters: most apps are singletons, so a
     * second `openPath` FOCUSES this window rather than mounting a new one.
     * An app that only reads `get()` at mount works exactly once.
     */
    launchParams: {
      // Read through the store, not the mount-time snapshot, so get() and
      // subscribe() can never disagree after a re-launch.
      get: (): Record<string, unknown> | undefined =>
        (windowId ? osStore.launchParamsFor(windowId) : undefined) ??
        opts?.launchParams,
      subscribe: (cb: (params: Record<string, unknown>) => void): (() => void) => {
        if (!windowId) return () => {};
        const raw = osStore.subscribeLaunchParams(windowId, cb);
        // Idempotent: the app may unsubscribe AND the host will on unmount.
        let disposed = false;
        const dispose = () => {
          if (disposed) return;
          disposed = true;
          raw();
        };
        opts?.registerCleanup?.(dispose);
        return dispose;
      },
    },
    storage: {
      get: (k: string) => localStorage.getItem(`mod:${k}`),
      set: (k: string, v: string) => localStorage.setItem(`mod:${k}`, v),
      remove: (k: string) => localStorage.removeItem(`mod:${k}`),
      clear: () => {},
    },
    hermes: {
      ensureSession: async () => {
        const res = await fetch("/api/sessions", {
          method: "POST",
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => ({}) as Record<string, unknown>);
        if (!res.ok) {
          // requireAgentAccess refuses keyless non-admins and guests HERE, at
          // app open, before a single message is sent. Returning `data.id`
          // (undefined) would hand the app an unusable session id and no way to
          // explain itself — so reject with the typed reason attached.
          const err = new Error(
            (data.error as string) || res.statusText || "Hermes is unavailable"
          ) as HermesAccessError;
          err.reason = data.reason as HermesErrorReason | undefined;
          err.status = res.status;
          throw err;
        }
        return data.id as string;
      },
      send: async (sessionId: string, content: string) => {
        osSocket.sendHermes(sessionId, content);
      },
      subscribe: (_sessionId: string, handler: StreamHandler) => osSocket.onHermes(handler),
    },
    theme: {
      getActiveId: () => osStore.themeId,
      setActive: async (id: string) => {
        const vars = await activateTheme(id);
        osStore.setTheme(id, vars);
      },
      getCssVars: () => getActiveThemeVars(),
      list: () => osStore.themeModules().map((m) => m.id),
      getUiScale: () => osStore.uiScale,
      setUiScale: (scale: number) => osStore.setUiScale(scale),
    },
    wallpaper: {
      getActiveId: () => osStore.wallpaperId,
      setActive: async (id: string) => osStore.setWallpaper(id),
      setPaused: (p: boolean) => osStore.setWallpaperPaused(p),
      list: () => osStore.wallpaperModules().map((m) => m.id),
      settings: buildWallpaperSettingsApi(),
      pointer: { get: getWallpaperPointer },
      // Lazy import on purpose: the panel imports uiApi from this module, and
      // a static import here would close that cycle at module-init time.
      openSettings: (moduleId?: string) => {
        void import("./wallpaper-settings-panel").then((m) =>
          m.openWallpaperSettingsPanel(moduleId)
        );
      },
    },
    notify: {
      push: (m: string, k?: "info" | "success" | "error" | "warn") =>
        osStore.notify(m, k || "info"),
      info: (m: string) => osStore.notify(m, "info"),
      success: (m: string) => osStore.notify(m, "success"),
      error: (m: string) => osStore.notify(m, "error"),
      warn: (m: string) => osStore.notify(m, "warn"),
    },
    osupdate: {
      /* Read from the SDK constant, never retyped. This said "0.2.0" as a
         literal while the server reported whatever ARCLOG_OS_VERSION said, so
         an app asking the OS its version could get a different answer from the
         one `GET /api/os/version` gave — and did, for about fifty commits. */
      currentVersion: () => ARCLOG_OS_VERSION,
      check: async () => {
        const res = await fetch("/api/os/update/check", { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data.updates || [];
      },
      download: async (updateId: string) => {
        const res = await fetch("/api/os/update/download", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ id: updateId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
      },
      stage: async (updateId: string) => {
        const res = await fetch("/api/os/update/stage", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ id: updateId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
      },
      install: async (updateId: string) => {
        const res = await fetch("/api/os/update/install", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ id: updateId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
      },
      apply: async (updateId: string) => {
        const res = await fetch("/api/os/update/apply", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ id: updateId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
      },
      rollback: async (updateId: string) => {
        const res = await fetch("/api/os/update/rollback", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ id: updateId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
      },
      status: async () => {
        const res = await fetch("/api/os/update/status", { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data.updates || [];
      },
      notifications: async () => {
        const res = await fetch("/api/os/update/notifications", { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data.notifications || [];
      },
      dismissNotification: async (id: string) => {
        const res = await fetch(
          "/api/os/update/notifications/" + encodeURIComponent(id) + "/dismiss",
          { method: "POST", headers: authHeaders() }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
      },
      snapshots: async () => {
        const res = await fetch("/api/os/update/snapshots", { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data.snapshots || [];
      },
      createSnapshot: async (label?: string) => {
        const res = await fetch("/api/os/update/snapshots", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ label: label || "Manual snapshot" }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
      },
      restoreSnapshot: async (snapshotId: string) => {
        const res = await fetch(
          "/api/os/update/snapshots/" + encodeURIComponent(snapshotId) + "/restore",
          { method: "POST", headers: authHeaders() }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
      },
    },
    workers: createWorkersApi(),

    packages: {
      list: async () => {
        await fetchPackages();
        return osStore.packages;
      },
      install: pkgInstall,
      uninstall: pkgUninstall,
      enable: async () => {},
      disable: async () => {},
      setDefaultVersion: pkgSetDefault,
      listVersions: pkgVersions,
      // install → migrate → advance default, with the migration running HERE
      // because ModuleContext only exists in the browser. See pkgUpdate: a
      // migration that throws rejects before the default is advanced, so the
      // old version keeps serving. updates() never rejects, by contract.
      updates: pkgUpdates,
      update: pkgUpdate,
    },
    wasm: {
      instantiate: async (moduleId: string, version?: string) => {
        try {
          const ref = version ? `${moduleId}@${version}` : moduleId;
          const res = await fetch(`/modules/${ref}/module.wasm`);
          if (!res.ok) return null;
          const buf = await res.arrayBuffer();
          return WebAssembly.instantiate(buf, {});
        } catch {
          return null;
        }
      },
    },
    permissions: {
      list: () => osStore.permissions,
      can: osCan,
    },
    graphics: graphicsApi,
    icons: iconsApi,
    ui: uiApi,
    charts: chartsApi,
    device: deviceApi,
    layout: layoutApi,
    time: timeApi,
    weather: weatherApi,
    widget: widgetApi,
    http: createHttpApi(appId),
    keys: createKeysApi(),
    agents: createAgentsApi(appId, () => ctxHolder),
    db: createDbApi(appId),
    logs: createLogsApi(appId),
    analytics: createAnalyticsApi(appId),
    shell: {
      dialog: {
        alert: (opts: { title?: string; message: string; okLabel?: string }) =>
          dialogStore.alert(opts),
        confirm: (opts: {
          title?: string;
          message: string;
          okLabel?: string;
          cancelLabel?: string;
          danger?: boolean;
        }) => dialogStore.confirm(opts),
        prompt: (opts: {
          title?: string;
          message?: string;
          defaultValue?: string;
          placeholder?: string;
          okLabel?: string;
          cancelLabel?: string;
        }) => dialogStore.prompt(opts),
        dateRange: (opts?: {
          title?: string;
          message?: string;
          defaultFrom?: number;
          defaultTo?: number;
          okLabel?: string;
          cancelLabel?: string;
        }) => dialogStore.dateRange(opts),
        openFile: (opts?: {
          title?: string;
          startPath?: string;
          filters?: { label: string; extensions: string[] }[];
        }) =>
          fileDialogStore.open({
            mode: "open-file",
            title: opts?.title,
            startPath: opts?.startPath,
            filters: opts?.filters,
          }),
        openFolder: (opts?: { title?: string; startPath?: string }) =>
          fileDialogStore.open({
            mode: "open-folder",
            title: opts?.title,
            startPath: opts?.startPath,
          }),
        saveFile: (opts?: {
          title?: string;
          startPath?: string;
          filename?: string;
          filters?: { label: string; extensions: string[] }[];
        }) =>
          fileDialogStore.open({
            mode: "save-file",
            title: opts?.title,
            startPath: opts?.startPath,
            filename: opts?.filename,
            filters: opts?.filters,
          }),
      },
      menus: {
        set: (menus: unknown) => {
          if (!windowId) return;
          appMenuStore.set(windowId, menus as never);
        },
        clear: () => {
          if (windowId) appMenuStore.clear(windowId);
        },
      },
      /**
       * Open a file in whichever enabled app declares its extension in
       * `manifest.fileTypes` (lowest dock.order wins, then id — the same file
       * always lands in the same app). The app receives `{ path }` as
       * ctx.launchParams.
       *
       * Never throws for "nothing handles this": the caller knows how to say so
       * in its own words, and `extension` is handed back for exactly that.
       */
      openPath: (
        path: string
      ): {
        opened: boolean;
        appId?: string;
        windowId?: string;
        extension: string;
      } => osStore.openPath(path),
      openContextMenu: (
        x: number,
        y: number,
        items: unknown[],
        meta?: { detail?: string }
      ) => {
        contextMenuStore.open(
          x,
          y,
          { kind: "app", appId, detail: meta?.detail },
          items as never
        );
      },
    },
    appState: {
      namespace: stateNs,
      get: (key: string, fallback?: unknown) => {
        try {
          const raw = localStorage.getItem(stateNs + "." + key);
          if (raw == null) return fallback;
          return JSON.parse(raw);
        } catch {
          return fallback;
        }
      },
      set: (key: string, value: unknown) => {
        localStorage.setItem(stateNs + "." + key, JSON.stringify(value));
      },
      remove: (key: string) => {
        localStorage.removeItem(stateNs + "." + key);
      },
    },
    docs: (() => {
      type RecentDoc = { id: string; title?: string; kind?: string; at: number };
      const CURRENT_KEY = stateNs + ".docs.current"; // the doc to reopen on launch
      const RECENT_KEY = stateNs + ".docs.recent"; // history for a Recent menu
      const REOPEN_KEY = stateNs + ".docs.reopen";
      const MAX_RECENT = 12;
      const basename = (id: string) => id.split("/").filter(Boolean).pop() || id;
      const readJson = <T>(key: string, fb: T): T => {
        try {
          const v = localStorage.getItem(key);
          return v == null ? fb : (JSON.parse(v) as T);
        } catch {
          return fb;
        }
      };
      const writeJson = (key: string, value: unknown) => {
        try {
          localStorage.setItem(key, JSON.stringify(value));
        } catch {
          /* quota / private mode */
        }
      };
      const readRecent = (): RecentDoc[] => {
        const v = readJson<RecentDoc[]>(RECENT_KEY, []);
        return Array.isArray(v) ? v : [];
      };
      const readCurrent = (): RecentDoc | null => readJson<RecentDoc | null>(CURRENT_KEY, null);
      const reopenOnLaunch = () => localStorage.getItem(REOPEN_KEY) !== "0";
      return {
        recent: () => readRecent(),
        last: () => readCurrent(),
        reopenOnLaunch,
        setReopenOnLaunch: (on: boolean) => {
          localStorage.setItem(REOPEN_KEY, on ? "1" : "0");
        },
        setCurrent: (doc: RecentDoc | string | null) => {
          if (!doc) return;
          const rec: RecentDoc =
            typeof doc === "string"
              ? { id: doc, title: basename(doc), at: Date.now() }
              : { title: basename(doc.id), ...doc, at: Date.now() };
          if (!rec.id) return;
          writeJson(CURRENT_KEY, rec);
          writeJson(RECENT_KEY, [rec, ...readRecent().filter((d) => d.id !== rec.id)].slice(0, MAX_RECENT));
        },
        clearCurrent: () => {
          // Stop reopening on launch; recents are kept for a Recent menu.
          try {
            localStorage.removeItem(CURRENT_KEY);
          } catch {
            /* */
          }
        },
        restoreLast: (open: (doc: RecentDoc) => void | Promise<void>): boolean => {
          if (!reopenOnLaunch()) return false;
          const cur = readCurrent();
          if (!cur) return false;
          try {
            void Promise.resolve(open(cur)).catch(() => {});
          } catch {
            /* app open handler threw synchronously */
          }
          return true;
        },
      };
    })(),
    moduleVersion: opts?.moduleVersion,
    user: {
      me: () => {
        try {
          const raw = localStorage.getItem("arclog.user");
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      },
    },
    fs: {
      /**
       * Directory entries. Dot-files are hidden by default (the server filters
       * them in `fsListAll`); pass `{ showHidden: true }` to get them, which is
       * what the Terminal's `ls -a` does. `.meta` is filtered server-side
       * either way — it is trash bookkeeping, not a user's file.
       */
      list: async (p: string, opts?: { showHidden?: boolean }) => {
        const qs = opts?.showHidden ? "&hidden=1" : "";
        const res = await fetch(`/api/fs/list?path=${encodeURIComponent(p)}${qs}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "list failed");
        return data.entries || [];
      },
      /**
       * Metadata for one path, or `null` when it does not exist.
       *
       * The endpoint answers 200 with `{entry:null}` for a missing path and
       * only 4xx's on a real failure (a path the caller may not touch), so
       * "missing" and "failed" stay distinguishable here — callers like the
       * shell's `cd`/`cat`/`rm` depend on exactly that split, and turning a
       * missing path into a throw would make `cd /nope` look like a broken VFS.
       */
      stat: async (p: string) => {
        const res = await fetch(`/api/fs/stat?path=${encodeURIComponent(p)}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "stat failed");
        return data.entry ?? null;
      },
      read: async (p: string) => {
        const res = await fetch(`/api/fs/read?path=${encodeURIComponent(p)}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "read failed");
        return data.content as string;
      },
      write: async (p: string, content: string) => {
        const res = await fetch("/api/fs/write", {
          method: "PUT",
          headers: authHeaders(),
          body: JSON.stringify({ path: p, content }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "write failed");
      },
      mkdir: async (p: string) => {
        const res = await fetch("/api/fs/mkdir", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ path: p }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "mkdir failed");
      },
      remove: async (p: string) => {
        const res = await fetch(`/api/fs?path=${encodeURIComponent(p)}`, {
          method: "DELETE",
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "remove failed");
      },
      /**
       * Move/rename. The server (`fsMove`) moves INTO `to` when `to` is an
       * existing directory, and otherwise treats `to` as the new full path.
       * It **refuses to overwrite**: an existing destination throws
       * `destination exists`. Delete the destination first if you mean to
       * clobber it — the SDK will not do it for you.
       */
      move: async (from: string, to: string) => {
        const res = await fetch("/api/fs/move", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ from, to }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "move failed");
      },
      /**
       * Copy. Same into-a-directory rule as {@link move}, but the collision
       * behaviour is the opposite: `fsCopy` never throws on an existing
       * destination — it silently picks a free name ("notes 2.txt"). That is
       * why this returns the resulting entry: it is the ONLY way to learn where
       * the copy actually landed. Check for the destination yourself first if
       * you need an exact path.
       */
      copy: async (from: string, to: string) => {
        const res = await fetch("/api/fs/copy", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ from, to }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "copy failed");
        return data.entry ?? null;
      },
    },
  } satisfies ModuleContext;
  ctxHolder = ctx;
  return ctx;
}
